import React, { FC, UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';
import ObrasAPI from '../../../../../services/APIObras';
import TitleValue from '../../../components/titleValue/titleValue';
import styles from './collaboratingCompanyAutocomplete.module.scss';

export interface CollaboratingCompanyOption {
  id: string;
  name: string;
  source: 'wanda' | 'phia';
  create?: boolean;
}

interface Props {
  id: string;
  label: string;
  value?: CollaboratingCompanyOption | null;
  error?: boolean;
  onChange: (option: CollaboratingCompanyOption) => void;
}

const normalize = (value: string) => value
  .trim()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .toUpperCase();

const PER_PAGE = 25;
const LOAD_MORE_THRESHOLD_PX = 48;

const getOptionKey = (option: CollaboratingCompanyOption) => `${option.source}-${option.id || option.name}`;

const mergeOptions = (
  currentOptions: CollaboratingCompanyOption[],
  newOptions: CollaboratingCompanyOption[],
) => {
  const existingKeys = new Set(currentOptions.map(getOptionKey));
  const uniqueNewOptions = newOptions.filter((option) => {
    const key = getOptionKey(option);
    if (existingKeys.has(key)) return false;
    existingKeys.add(key);
    return true;
  });
  return currentOptions.concat(uniqueNewOptions);
};

/* MUI supplies renderer props; react/prop-types incorrectly treats them as component props. */
/* eslint-disable react/prop-types */
const CollaboratingCompanyAutocomplete: FC<Props> = ({ id, label, value, error, onChange }) => {
  const [t] = useTranslation();
  const [options, setOptions] = useState<CollaboratingCompanyOption[]>([]);
  const [inputValue, setInputValue] = useState(value?.name || '');
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [completedQuery, setCompletedQuery] = useState<string | null>(null);
  const requestId = useRef(0);

  const loading = loadingInitial || loadingMore;

  useEffect(() => {
    setInputValue(value?.name || '');
  }, [value?.name]);

  const fetchOptions = useCallback(async (text: string, nextPage = 0, append = false) => {
    const currentRequestId = requestId.current + 1;
    const queryText = text.trim().length < 3 ? '' : text.trim();
    requestId.current = currentRequestId;
    setLoadingMore(append);
    setLoadingInitial(!append);
    try {
      const response = await ObrasAPI.getInstance().getCollaboratingCompaniesAPI(queryText, nextPage, PER_PAGE);
      if (requestId.current !== currentRequestId) return;

      setPage(response.page);
      setSearchFailed(false);
      setCompletedQuery(text.trim());
      setHasExactMatch('hasExactMatch' in response && !!response.hasExactMatch);
      setHasNextPage(response.hasNextPage);
      setOptions((currentOptions) => append ? mergeOptions(currentOptions, response.data) : response.data);
    } catch (err) {
      if (requestId.current !== currentRequestId) return;
      setSearchFailed(true);
      if (!append) setOptions([]);
      setHasNextPage(false);
    } finally {
      if (requestId.current === currentRequestId) {
        append ? setLoadingMore(false) : setLoadingInitial(false);
      }
    }
  }, []);

  const debouncedFetchOptions = useMemo(
    () => debounce((text: string) => fetchOptions(text, 0, false), 500),
    [fetchOptions],
  );

  useEffect(() => () => debouncedFetchOptions.cancel(), [debouncedFetchOptions]);

  const handleOpen = () => {
    setOpen(true);
    if (!options.length || searchFailed || completedQuery !== inputValue.trim()) {
      fetchOptions(inputValue, 0, false);
    }
  };

  const handleListboxScroll = (event: UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    const isNearBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - LOAD_MORE_THRESHOLD_PX;

    if (isNearBottom && hasNextPage && !loading) {
      fetchOptions(inputValue, page + 1, true);
    }
  };

  return (
    <TitleValue
      identifier={id}
      title={label}
      value={inputValue}
      editable
      type='autocomplete'
      error={error}
    >
      <Autocomplete
        id={id}
        size="small"
        value={value || null}
        inputValue={inputValue}
        options={options}
        open={open}
        loading={loadingInitial}
        loadingText={t('details.clients.searchingEntities')}
        noOptionsText={searchFailed ? t('details.clients.searchFailed') : t('input.noResults')}
        clearOnBlur={false}
        openOnFocus
        selectOnFocus
        handleHomeEndKeys
        ListboxProps={{ onScroll: handleListboxScroll }}
        filterOptions={(availableOptions) => {
          const trimmedInput = inputValue.trim();
          const hasVisibleExactMatch = availableOptions.some((option) => normalize(option.name) === normalize(trimmedInput));
          if (!searchFailed && !loading && completedQuery === trimmedInput && trimmedInput.length >= 3
            && !hasExactMatch && !hasVisibleExactMatch) {
            return availableOptions.concat({
              id: '',
              name: trimmedInput,
              source: 'phia',
              create: true,
            });
          }
          return availableOptions;
        }}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
        isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id && option.name === selectedValue.name}
        onOpen={handleOpen}
        onClose={() => setOpen(false)}
        onInputChange={(_, newInputValue) => {
          requestId.current += 1;
          setCompletedQuery(null);
          setLoadingInitial(false);
          setLoadingMore(false);
          setInputValue(newInputValue);
          setPage(0);
          setHasNextPage(false);
          debouncedFetchOptions(newInputValue);
        }}
        onChange={(_, selectedOption) => {
          if (!selectedOption) return;
          onChange(selectedOption);
        }}
        renderOption={(props, option) => (
          <li
            {...props}
            key={getOptionKey(option)}
            className={`${props.className || ''} ${option.create ? styles.createOption : styles.option}`}
          >
            {option.create && <AddIcon className={styles.createIcon} fontSize="small" />}
            <span className={styles.optionText}>
              <span className={styles.optionName}>
                {option.create ? t('details.clients.createEntity', { name: option.name }) : option.name}
              </span>
            </span>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={String(t('details.clients.searchOrCreateEntity'))}
            error={error}
            variant="standard"
            inputProps={{ ...params.inputProps, maxLength: 255 }}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </TitleValue>
  );
};
/* eslint-enable react/prop-types */

export default CollaboratingCompanyAutocomplete;
